/**
 * Workspaces Data View - 워크스페이스별 데이터 분석
 */

import { Trans, t } from '@lingui/macro';
import {
    Building2, MessageSquare, Users, HardDrive, RefreshCw,
    Loader2, AlertTriangle, CheckCircle, Clock, ChevronRight
} from 'lucide-react';
import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Fire, getFunc } from '../../services/FireClient';
import { collection, getDocs, query, where, doc, getDoc, orderBy, limit } from 'firebase/firestore';

interface WorkspaceData {
    id: string;
    name: string;
    tier: string;
    roomCount: number;
    memberCount: number;
    // Storage from WorkspaceStatus
    filesMB: number;
    screenRecordingsMB: number;
    aiTrainingDocsMB: number;
    // Storage from usage_stats
    usageStatsBytes: number;
    // Discrepancy
    hasDiscrepancy: boolean;
    lastUpdated?: Date;
}

const DevAdminWorkspaces: React.FC = () => {
    const [workspaces, setWorkspaces] = useState<WorkspaceData[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [recalculating, setRecalculating] = useState<string | null>(null);

    useEffect(() => {
        loadWorkspaces();
    }, []);

    const loadWorkspaces = async () => {
        setLoading(true);
        try {
            const store = Fire.store;
            const wsSnap = await getDocs(collection(store, 'workspaces'));

            const results: WorkspaceData[] = [];

            for (const wsDoc of wsSnap.docs) {
                const ws = wsDoc.data();

                // Get room count
                const roomsSnap = await getDocs(
                    query(collection(store, 'rooms'), where('workspaceId', '==', wsDoc.id))
                );

                // Get member count
                const membersSnap = await getDocs(
                    collection(store, 'workspaces', wsDoc.id, 'members')
                );

                // Get WorkspaceStatus (from scheduled scan)
                let filesMB = 0, screenRecordingsMB = 0, aiTrainingDocsMB = 0;
                let statusLastUpdated: Date | undefined;
                try {
                    const statusDoc = await getDoc(
                        doc(store, 'workspaces', wsDoc.id, 'status', wsDoc.id)
                    );
                    if (statusDoc.exists()) {
                        const status = statusDoc.data();
                        filesMB = status.filesMB || 0;
                        screenRecordingsMB = status.screenRecordingsMB || 0;
                        aiTrainingDocsMB = status.aiTrainingDocsMB || 0;
                        statusLastUpdated = status.lastUpdated?.toDate?.();
                    }
                } catch (e) {
                    console.error('Failed to load status for', wsDoc.id);
                }

                // Get usage_stats (from event-based tracking)
                let usageStatsBytes = 0;
                try {
                    const usageDoc = await getDoc(
                        doc(store, 'workspaces', wsDoc.id, 'usage_stats', 'current')
                    );
                    if (usageDoc.exists()) {
                        usageStatsBytes = usageDoc.data().storageBytes || 0;
                    }
                } catch (e) {
                    console.error('Failed to load usage_stats for', wsDoc.id);
                }

                // Calculate total from status
                const totalStatusMB = filesMB + screenRecordingsMB + aiTrainingDocsMB;
                const usageStatsMB = usageStatsBytes / (1024 * 1024);

                // Check for discrepancy (>10% difference)
                const hasDiscrepancy = totalStatusMB > 0 &&
                    Math.abs(totalStatusMB - usageStatsMB) / totalStatusMB > 0.1;

                results.push({
                    id: wsDoc.id,
                    name: ws.name || 'Unknown',
                    tier: ws.tier || 'FREE',
                    roomCount: roomsSnap.size,
                    memberCount: membersSnap.size,
                    filesMB,
                    screenRecordingsMB,
                    aiTrainingDocsMB,
                    usageStatsBytes,
                    hasDiscrepancy,
                    lastUpdated: statusLastUpdated,
                });
            }

            // Sort by storage size
            results.sort((a, b) =>
                (b.filesMB + b.screenRecordingsMB + b.aiTrainingDocsMB) -
                (a.filesMB + a.screenRecordingsMB + a.aiTrainingDocsMB)
            );

            setWorkspaces(results);
        } catch (error) {
            console.error('Failed to load workspaces:', error);
        } finally {
            setLoading(false);
        }
    };

    const recalculateStorage = async (wsId: string) => {
        setRecalculating(wsId);
        try {
            // Call the scheduled function manually
            const updateStatus = getFunc('updateWorkspaceStatusManual');
            await updateStatus({ workspaceId: wsId });
            // Reload data
            await loadWorkspaces();
        } catch (error) {
            console.error('Failed to recalculate:', error);
            alert('재계산 실패: Cloud Function이 배포되어 있지 않거나 권한이 없습니다.');
        } finally {
            setRecalculating(null);
        }
    };

    const formatMB = (mb: number) => {
        if (mb >= 1000) return `${(mb / 1000).toFixed(1)} GB`;
        return `${mb.toFixed(1)} MB`;
    };

    const filteredWorkspaces = workspaces.filter(ws =>
        ws.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        ws.id.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <div className="flex flex-col h-full">
            {/* Header */}
            <div className="bg-white/80 backdrop-blur-md border-b border-slate-100/50 px-6 py-4">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-xl font-bold text-slate-900">
                            <Trans>Workspaces Data</Trans>
                        </h1>
                        <p className="text-sm text-slate-500 mt-0.5">
                            <Trans>Storage usage and data integrity check</Trans>
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        <Input
                            placeholder={t`Search workspaces...`}
                            className="w-64"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                        <Button onClick={loadWorkspaces} variant="outline" size="sm">
                            <RefreshCw className="w-4 h-4" />
                        </Button>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto p-6 bg-slate-50/30">
                {loading ? (
                    <div className="flex items-center justify-center py-20">
                        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                    </div>
                ) : (
                    <div className="space-y-3">
                        {/* Summary */}
                        <div className="flex items-center gap-4 mb-4">
                            <Badge variant="outline">
                                Total: {workspaces.length} workspaces
                            </Badge>
                            <Badge variant="destructive" className={
                                workspaces.filter(ws => ws.hasDiscrepancy).length === 0 ? 'hidden' : ''
                            }>
                                {workspaces.filter(ws => ws.hasDiscrepancy).length} with discrepancy
                            </Badge>
                        </div>

                        {/* Workspace List */}
                        {filteredWorkspaces.map((ws) => (
                            <Card key={ws.id} className={`overflow-hidden ${ws.hasDiscrepancy ? 'ring-2 ring-amber-400' : ''}`}>
                                <CardContent className="p-0">
                                    <div className="flex items-center">
                                        <div className="p-4 flex-1">
                                            <div className="flex items-center gap-3 mb-2">
                                                <Building2 className="w-5 h-5 text-slate-400" />
                                                <span className="font-semibold text-slate-900">{ws.name}</span>
                                                <Badge variant="secondary">{ws.tier}</Badge>
                                                {ws.hasDiscrepancy && (
                                                    <Badge variant="destructive" className="gap-1">
                                                        <AlertTriangle className="w-3 h-3" />
                                                        불일치
                                                    </Badge>
                                                )}
                                            </div>
                                            <div className="text-xs text-slate-500 mb-3">{ws.id}</div>

                                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                                <div className="flex items-center gap-2">
                                                    <MessageSquare className="w-4 h-4 text-green-500" />
                                                    <span>{ws.roomCount} rooms</span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <Users className="w-4 h-4 text-blue-500" />
                                                    <span>{ws.memberCount} members</span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <HardDrive className="w-4 h-4 text-purple-500" />
                                                    <span>Status: {formatMB(ws.filesMB + ws.screenRecordingsMB + ws.aiTrainingDocsMB)}</span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <HardDrive className="w-4 h-4 text-orange-500" />
                                                    <span>Events: {formatMB(ws.usageStatsBytes / (1024 * 1024))}</span>
                                                </div>
                                            </div>

                                            {ws.lastUpdated && (
                                                <div className="text-xs text-slate-400 mt-2 flex items-center gap-1">
                                                    <Clock className="w-3 h-3" />
                                                    Last scan: {ws.lastUpdated.toLocaleString()}
                                                </div>
                                            )}
                                        </div>
                                        <div className="p-4 border-l border-slate-100">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => recalculateStorage(ws.id)}
                                                disabled={recalculating === ws.id}
                                            >
                                                {recalculating === ws.id ? (
                                                    <Loader2 className="w-4 h-4 animate-spin" />
                                                ) : (
                                                    <RefreshCw className="w-4 h-4" />
                                                )}
                                            </Button>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default DevAdminWorkspaces;
